// app/javascript/components/Chat/DataOutput.js
import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  IconButton,
  Typography,
  Drawer,
  SwipeableDrawer,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useChatContext } from "../../contexts/ChatContext";
import { useAppContext } from "../../contexts/AppContext";
import { LoadingPhasesBox } from "./ResponseWaiting";
import { isDarkMode } from "../../utils/constants";
import MarketProLogo from "../../../javascript/images/MarketProLogo.png";
import MarketProLogoLight from "../../../javascript/images/MarketProLogoLight.png";
import { keyframes } from "@emotion/react";
import { getLoadingPhaseMeta } from "./loadingPhaseVisuals";
import {
  buildProcessRecords,
  processRecordPreviewText,
} from "./loadingPhaseProcessModel";
import AnalysisTracePanel from "./AnalysisTracePanel";
import ProcessQueueCard from "./DataOutputProcessQueueCard";
import {
  DATA_PROCESSES_MAX_WIDTH,
  DATA_PROCESSES_MIN_WIDTH,
  getDataProcessesWidth,
} from "../../hooks/useChatLayout";

const pulse = keyframes`
  0%   { transform: scale(0.65); opacity: 0.5; }
  70%  { transform: scale(1.0); opacity: 0; }
  100% { transform: scale(1.0); opacity: 0; }
`;

export const DataOutputPanelContent = ({
  loadingResponse,
  loadingPhaseMessage,
  dataProcessesQueue,
  analysisTraceEvents,
  activeThread,
  liveTextOutputting,
  userData,
}) => {
  const canUseAdminLoadingPhases = userData?.is_admin_user === true;
  const hasAnalysisTrace =
    canUseAdminLoadingPhases &&
    Array.isArray(analysisTraceEvents) &&
    analysisTraceEvents.length > 0;

  const [doneByMsg, setDoneByMsg] = useState(() => new Map());
  const seenIdsRef = useRef(new Set());
  const prevThreadUuidRef = useRef(null);

  const activeThreadUuid = activeThread?.threadUuid ?? null;
  const dataProcessesQueueLength = dataProcessesQueue?.length || 0;
  const showProcessingLoader = loadingResponse && !liveTextOutputting;

  useEffect(() => {
    if (!dataProcessesQueue?.length) return;
    for (const evt of dataProcessesQueue) {
      if (!evt?.id) continue;
      if (!seenIdsRef.current.has(evt.id)) {
        seenIdsRef.current.add(evt.id);
        if (evt.text.complete || evt.text.require_completion == false) {
          let timeout = 800;
          if (evt.text.require_completion == false) timeout = 1300;
          setTimeout(() => {
            setDoneByMsg((prevMap) => {
              const m = new Map(prevMap);
              m.set(evt.text.message, true);
              return m;
            });
          }, timeout);
        }
      }
    }
  }, [dataProcessesQueue]); // depend on the array reference, not just length

  useEffect(() => {
    if (loadingResponse && dataProcessesQueueLength === 0) {
      seenIdsRef.current = new Set();
      setDoneByMsg(new Map());
    }
  }, [dataProcessesQueueLength, loadingResponse]);

  useEffect(() => {
    const curr = activeThreadUuid;
    const prev = prevThreadUuidRef.current;
    // if thread changes, reset it (If it doesn't change from a temp thread).
    if (prev === curr) return; // only run if thread uuid has changed..
    const prevWasTemp = !!prev && prev.startsWith("temp-");
    if (!prevWasTemp) {
      if (activeThread) {
        seenIdsRef.current = new Set();
        setDoneByMsg(new Map());
      }
    }
    prevThreadUuidRef.current = curr;
  }, [activeThread, activeThreadUuid]);

  const phaseMeta = getLoadingPhaseMeta(loadingPhaseMessage || "", isDarkMode);
  const processRecords = useMemo(
    () => buildProcessRecords(dataProcessesQueue || [], doneByMsg),
    [dataProcessesQueue, doneByMsg],
  );

  const legacyProcessContent = (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        py: 2,
        px: 1,
        pt: 1,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        overflowWrap: "anywhere",
        "& *": { whiteSpace: "pre-wrap", wordBreak: "break-word", overflowWrap: "anywhere" },
        "&::-webkit-scrollbar": { width: 8 },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: isDarkMode ? "#3a3a3a" : "#cfcfcf",
          borderRadius: 8,
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: isDarkMode ? "#1e1e1e" : "#f3f3f3",
        },
      }}
    >
      {processRecords.length === 0 && (
        <Box sx={{ pl: 1 }}>
          <LoadingPhasesBox
            text={loadingPhaseMessage ? "Awaiting next process." : "Awaiting analysis."}
            sx={{ marginTop: "8px" }}
            hideDotDotDot={!showProcessingLoader}
            preventAnimation={!showProcessingLoader}
          />
        </Box>
      )}

      {processRecords.map((record, idx) => {
        const isActive = showProcessingLoader && record.isActive;
        return (
          <ProcessQueueCard
            key={record.key || `${idx}-${record.message}`}
            text={record.label}
            detail={processRecordPreviewText(record, false)}
            done={record.done}
            isActive={isActive}
            phaseMeta={phaseMeta}
            enterIndex={idx}
          />
        );
      })}
    </Box>
  );

  return hasAnalysisTrace ? (
    <AnalysisTracePanel
      events={analysisTraceEvents}
      legacyContent={processRecords.length > 0 ? legacyProcessContent : null}
      loading={showProcessingLoader}
    />
  ) : legacyProcessContent;
};

const DataOutput = ({ loadingResponse }) => {
  const {
    loadingPhaseMessage,
    dataProcessesQueue,
    analysisTraceEvents,
    isDataProcessesOpen,
    setIsDataProcessesOpen,
    dataProcessesPanelWidth,
    setDataProcessesPanelWidth,
    activeThread,
    liveTextOutputting,
  } =
    useChatContext();
  const { appProps } = useAppContext();

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const userData = appProps?.userData;
  const canUseAdminLoadingPhases = userData?.is_admin_user === true;
  const hasAnalysisTrace =
    canUseAdminLoadingPhases &&
    Array.isArray(analysisTraceEvents) &&
    analysisTraceEvents.length > 0;
  const panelWidth = getDataProcessesWidth(
    hasAnalysisTrace,
    dataProcessesPanelWidth,
    typeof window === "undefined" ? null : window.innerWidth,
  );

  const [isResizingPanel, setIsResizingPanel] = useState(false);
  const autoOpenedTraceIdsRef = useRef(new Set());
  const panelResizeCleanupRef = useRef(null);

  const activeThreadUuid = activeThread?.threadUuid ?? null;
  const showProcessingLoader = loadingResponse && !liveTextOutputting;
  const currentTraceId = hasAnalysisTrace
    ? (analysisTraceEvents.find((event) => event?.trace_id)?.trace_id || activeThreadUuid || "active-trace")
    : null;

  const handleResizePointerDown = (event) => {
    if (!isDesktop || typeof window === "undefined") return;
    event.preventDefault();
    panelResizeCleanupRef.current?.();

    const body = document.body;
    const previousCursor = body.style.cursor;
    const previousUserSelect = body.style.userSelect;

    setIsResizingPanel(true);
    body.style.cursor = "col-resize";
    body.style.userSelect = "none";

    const updateWidth = (pointerEvent) => {
      const nextWidth = getDataProcessesWidth(
        hasAnalysisTrace,
        window.innerWidth - pointerEvent.clientX,
        window.innerWidth,
      );
      setDataProcessesPanelWidth(nextWidth);
    };

    const stopResize = () => {
      setIsResizingPanel(false);
      body.style.cursor = previousCursor;
      body.style.userSelect = previousUserSelect;
      window.removeEventListener("pointermove", updateWidth);
      window.removeEventListener("pointerup", stopResize);
      window.removeEventListener("pointercancel", stopResize);
      panelResizeCleanupRef.current = null;
    };

    panelResizeCleanupRef.current = stopResize;
    updateWidth(event);
    window.addEventListener("pointermove", updateWidth);
    window.addEventListener("pointerup", stopResize);
    window.addEventListener("pointercancel", stopResize);
  };

  const handleResizeKeyDown = (event) => {
    if (!isDesktop || typeof window === "undefined") return;

    const step = event.shiftKey ? 64 : 24;
    let nextWidth = null;
    if (event.key === "ArrowLeft") nextWidth = panelWidth + step;
    if (event.key === "ArrowRight") nextWidth = panelWidth - step;
    if (event.key === "Home") nextWidth = DATA_PROCESSES_MIN_WIDTH;
    if (event.key === "End") nextWidth = DATA_PROCESSES_MAX_WIDTH;
    if (nextWidth == null) return;

    event.preventDefault();
    setDataProcessesPanelWidth(
      getDataProcessesWidth(hasAnalysisTrace, nextWidth, window.innerWidth),
    );
  };

  useEffect(() => {
    return () => {
      panelResizeCleanupRef.current?.();
    };
  }, []);

  useEffect(() => {
    if (!isDesktop) return;
    if (!showProcessingLoader) return;
    if (!hasAnalysisTrace) return;
    if (!currentTraceId) return;
    if (autoOpenedTraceIdsRef.current.has(currentTraceId)) return;

    autoOpenedTraceIdsRef.current.add(currentTraceId);
    if (isDataProcessesOpen !== true) {
      setIsDataProcessesOpen(true);
    }
  }, [currentTraceId, hasAnalysisTrace, isDataProcessesOpen, isDesktop, setIsDataProcessesOpen, showProcessingLoader]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const nextWidth = getDataProcessesWidth(
      hasAnalysisTrace,
      dataProcessesPanelWidth,
      window.innerWidth,
    );
    if (dataProcessesPanelWidth && nextWidth !== dataProcessesPanelWidth) {
      setDataProcessesPanelWidth(nextWidth);
    }
  }, [dataProcessesPanelWidth, hasAnalysisTrace, setDataProcessesPanelWidth]);

  const processContent = (
    <DataOutputPanelContent
      loadingResponse={loadingResponse}
      loadingPhaseMessage={loadingPhaseMessage}
      dataProcessesQueue={dataProcessesQueue}
      analysisTraceEvents={analysisTraceEvents}
      activeThread={activeThread}
      liveTextOutputting={liveTextOutputting}
      userData={userData}
    />
  );

  return (
    <>
      {/* Floating launcher */}
      <Box
        sx={{
          position: "fixed",
          top: 8,
          right: 8,
          zIndex: (theme) => theme.zIndex.drawer + 2,
          pointerEvents: "none",
        }}
      >
        <IconButton
          onClick={() => setIsDataProcessesOpen((o) => !o)}
          sx={{
            pointerEvents: "auto",
            position: "relative",
            "&:hover": {
              backgroundColor: isDarkMode ? "#2b2b2b" : "#D6D6D6",
            },
            ...(showProcessingLoader && {
              // subtle pulsing ring
              "&::after": {
                content: '""',
                position: "absolute",
                inset: -4,
                borderRadius: "50%",
                border: isDarkMode ? "3px solid rgba(255,255,255,0.55)" : "2px solid rgba(0,0,0,0.45)",
                animation: `${pulse} 1.5s ease-out infinite`,
                pointerEvents: "none",
              },
            }),
          }}
          aria-label={
            isDataProcessesOpen ? "Collapse data output" : "Expand data output"
          }
          size="small"
        >
          <Box
            component="img"
            src={isDarkMode ? MarketProLogo : MarketProLogoLight}
            sx={{
              width: "36px",
              animation: "none",
            }}
            className="logo-img"
            alt="IncitePlus Logo"
          />
        </IconButton>
      </Box>

    {isDesktop ? (
      <Drawer
        anchor="right"
        variant="persistent"
        open={isDataProcessesOpen}
        sx={{
          "& .MuiDrawer-paper": {
            width: panelWidth,
            backgroundColor: isDarkMode ? "#1e1e1e" : "#F9F9F9",
            borderLeft: `1px solid ${isDarkMode ? "#2f2f2f" : "#e7e7e7"}`,
            boxShadow: isDarkMode
              ? "rgba(0,0,0,0.4) 0px 10px 30px"
              : "rgba(0,0,0,0.08) 0px 10px 30px",
            display: "flex",
            flexDirection: "column",
            transition: isResizingPanel ? "none" : "width 120ms ease",
          },
        }}
      >
        <Box
          aria-label="Resize data processes panel"
          aria-orientation="vertical"
          role="separator"
          tabIndex={0}
          title={`Drag to resize (${DATA_PROCESSES_MIN_WIDTH}-${DATA_PROCESSES_MAX_WIDTH}px)`}
          onKeyDown={handleResizeKeyDown}
          onPointerDown={handleResizePointerDown}
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: 10,
            zIndex: 4,
            cursor: "col-resize",
            touchAction: "none",
            "&::before": {
              content: '""',
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: 2,
              backgroundColor: isResizingPanel
                ? "#2563EB"
                : (isDarkMode ? "rgba(148,163,184,0.22)" : "rgba(71,85,105,0.18)"),
              transition: "background-color 140ms ease, width 140ms ease",
            },
            "&:hover::before": {
              width: 3,
              backgroundColor: "#2563EB",
            },
          }}
        />
        {/* Title Bar */}
        <Box
          sx={{
            p: 2,
            borderBottom: `1px solid ${isDarkMode ? "#2f2f2f" : "#e7e7e7"}`,
            backgroundColor: isDarkMode ? "#222222" : "#F9F9F9",
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 400, fontSize: "14px", letterSpacing: 0.5, color: isDarkMode ? "#f0f0f0" : "#111" }}
          >
            {hasAnalysisTrace ? "Analysis Trace" : "Data Processes"}
          </Typography>
        </Box>
        {processContent}
      </Drawer>
    ) : (
      <SwipeableDrawer
        anchor="right"
        open={isDataProcessesOpen}
        onOpen={() => setIsDataProcessesOpen(true)}
        onClose={() => setIsDataProcessesOpen(false)}
        swipeAreaWidth={24}
        keepMounted
        sx={{
          "& .MuiDrawer-paper": {
            width: `min(92vw, ${panelWidth}px)`,
            backgroundColor: isDarkMode ? "#1e1e1e" : "#F9F9F9",
            borderLeft: `1px solid ${isDarkMode ? "#2f2f2f" : "#e7e7e7"}`,
            boxShadow: isDarkMode
              ? "rgba(0,0,0,0.4) 0px 10px 30px"
              : "rgba(0,0,0,0.08) 0px 10px 30px",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* Title Bar */}
        <Box
          sx={{
            p: 2,
            borderBottom: `1px solid ${isDarkMode ? "#2f2f2f" : "#e7e7e7"}`,
            backgroundColor: isDarkMode ? "#222222" : "#F9F9F9",
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 400, fontSize: "14px", letterSpacing: 0.5, color: isDarkMode ? "#f0f0f0" : "#111" }}
          >
            {hasAnalysisTrace ? "Analysis Trace" : "Data Processes"}
          </Typography>
        </Box>
        {processContent}
      </SwipeableDrawer>
    )}
    </>
  );
};

export default DataOutput;
